/**
 * CORREGIR A MANO UN RENGLÓN DE LA HOJA DE PRESUPUESTO — las reglas, en un solo lugar.
 *
 * Por qué existe. Desde la migración 083 el renglón que no se pudo leer entra a la hoja
 * MARCADO, con la descripción cortada a 60–80 letras y una cantidad que puede no ser la del
 * documento, y el aviso le pide al cotizador que lo compare contra el original y lo
 * corrija. Le pedíamos que corrigiera y no le dábamos con qué. Acá viven las reglas de qué
 * cuenta como corrección y qué consecuencia tiene cada una.
 *
 * Por qué en `shared` y no en el servidor: el BOTÓN tiene que decir lo que va a pasar
 * ("Guardar" vs "Guardar y buscar el precio"), así que la pantalla necesita la misma regla
 * que el servidor va a aplicar. Con la regla en dos lados se despegan en el primer cambio y
 * el botón promete algo que no ocurre.
 */

export interface RenglonValores {
  descripcion: string;
  unidad: string;
  cantidad: number;
}

export interface CambiosDelRenglon {
  descripcion: boolean;
  unidad: boolean;
  cantidad: boolean;
  /** Alguno de los tres cambió de verdad. */
  hayCambios: boolean;
}

/** Espacios de más y espacios al principio/final no son una corrección. */
export function normalizarTexto(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

/**
 * La cantidad se guarda con 4 decimales, así que la comparación se hace con esa misma
 * precisión: escribir "2" donde ya decía "2.0000" no es un cambio.
 */
export function mismaCantidad(a: number, b: number): boolean {
  const r = (n: number) => Math.round((Number.isFinite(n) ? n : 0) * 10_000);
  return r(a) === r(b);
}

export function cambiosDelRenglon(actual: RenglonValores, nuevo: RenglonValores): CambiosDelRenglon {
  const descripcion = normalizarTexto(actual.descripcion) !== normalizarTexto(nuevo.descripcion);
  const unidad = normalizarTexto(actual.unidad) !== normalizarTexto(nuevo.unidad);
  const cantidad = !mismaCantidad(actual.cantidad, nuevo.cantidad);
  return { descripcion, unidad, cantidad, hayCambios: descripcion || unidad || cantidad };
}

export interface EstadoDelRenglon {
  /** Ya tiene un análisis de precio hecho con inteligencia artificial. */
  tieneAnalisisIa: boolean;
  /** Todavía no tiene precio (entró marcado, o no se le encontró equivalente). */
  sinPrecio: boolean;
}

/**
 * ¿Hay que volver a buscar el equivalente en los catálogos?
 *
 *  · La DESCRIPCIÓN y la UNIDAD obligan a buscar de nuevo: la equivalencia que había era
 *    para el texto viejo. La unidad pesa igual que la descripción porque es la que decide
 *    contra qué fila del catálogo se compara el precio.
 *  · Cambiar solo la CANTIDAD no cambia contra qué se compara: el precio por unidad es el
 *    mismo y lo único que se mueve es el importe (y con él el total de la hoja).
 *  · Salvo que el renglón todavía no tenga precio: ahí no hay nada que conservar, buscar no
 *    cuesta y es lo que el cotizador espera después de corregirlo.
 *  · Un renglón con análisis hecho con inteligencia artificial NO se re-busca solo: ese
 *    análisis se pagó y reemplazarlo por un precio de catálogo sin que nadie lo pida sería
 *    tirar trabajo. Volver a armarlo sigue donde estaba, en su propio botón.
 */
export function requiereNuevaBusqueda(cambios: CambiosDelRenglon, estado: EstadoDelRenglon): boolean {
  if (!cambios.hayCambios) return false;
  if (estado.tieneAnalisisIa) return false;
  if (cambios.descripcion || cambios.unidad) return true;
  return estado.sinPrecio;
}

/**
 * MARCADORES INTERNOS DE LA LECTURA (`[R:12]`). Algunas hojas los llevan pegados al final de
 * la descripción; la pantalla no los muestra, así que el texto que el usuario edita viene
 * sin ellos. Si se guardara tal cual, el marcador desaparecería sin que nadie lo decidiera:
 * acá se vuelven a pegar al final, en el mismo orden, cuando el texto nuevo no los trae.
 */
export function conservarMarcadores(anterior: string, nuevo: string): string {
  const marcas = anterior.match(/\[R:\d+\]/g);
  if (!marcas || marcas.length === 0) return nuevo;
  if (/\[R:\d+\]/.test(nuevo)) return nuevo;
  return `${nuevo.trim()} ${marcas.join(' ')}`.trim();
}
